import { ChainId } from '@uniswap/sdk';
import { IchiInsights } from 'common/models/Insights';
import { ICHI_LEGACY_TOKENS } from 'constants/constants';
import { isIchiContract, isIchiStakingContract } from 'constants/contracts';
import numbro from 'numbro';
import { ContractUtils } from 'utils/contractUtils';
import { CryptoUtils, GAS_SETTINGS } from 'utils/cryptoUtils';
import {
  isErrorWithCode,
  isUserRejectedError,
  isViemBaseError,
} from 'utils/errorUtils';
import { NumberUtils } from 'utils/numberUtils';
import { StorageUtils } from 'utils/storageUtils';
import { Nullable } from 'common/models/optional';
import { Address, BaseError, UserRejectedRequestError } from 'viem';
import { TokenUtils } from 'utils/tokenUtils';
import { StakeEvent } from 'xstate/stakeEvent';
import { readContract, writeContract } from '@wagmi/core';

export namespace StakeApi {
  export type StakeResponse = {
    message?: string;
  };
  export type UnstakeResponse = {
    message?: string;
  };
  export type StakeArgs = {
    amount: string;
    chainId: number;
    accountAddress: Address;
  };
  export type GetStakeDataArgs = {
    amount: string;
    chainId: number;
    accountAddress: Address;
  };
  export type GetUnstakeDataArgs = {
    amount: string;
    chainId: number;
    accountAddress: Address;
  };
  export type UnstakeArgs = {
    amount: string;
    chainId: number;
    accountAddress: Address;
  };
  export type GetStakeDataResponse = {
    ichiBalance: bigint;
    ichiAllowance: bigint;
    isIchiApproved: boolean;
    isSufficientBalance: boolean;
    ichiPriceUsd: number;
  };
  export type GetUnstakeDataResponse = {
    xIchiBalance: bigint;
    xIchiPriceUsd: number;
    isSufficientBalance: boolean;
  };

  export interface IStake {
    stake(args: StakeArgs): Promise<Nullable<StorageUtils.StoredTx>>;
    unstake(args: UnstakeArgs): Promise<Nullable<StorageUtils.StoredTx>>;
    getStakeData(args: GetStakeDataArgs): Promise<GetStakeDataResponse>;
    getUnstakeData(args: GetUnstakeDataArgs): Promise<GetUnstakeDataResponse>;
    resetIchiAllowance(args: GetStakeDataArgs): Promise<boolean>;
    approveIchiAllowance(
      args: GetStakeDataArgs
    ): Promise<Nullable<StorageUtils.StoredTx>>;
  }

  export class Stake implements IStake {
    private static instance: IStake;

    static maxApproval = 2 ** 256 - 1;
    // These will primarily be used for local storage lookups for determining if there
    // are in-flight transactions
    static stakeFN = 'stake';
    static stakeFNLabel = 'Stake';
    static unstakeFN = 'unstake';
    static unstakeFNLabel = 'Unstake';
    static resetIchiAllowanceFN = 'resetIchiAllowance';
    static resetIchiAllowanceFNLabel = 'Reset Alowance';
    static approveIchiAllowanceFN = 'approveIchiAllowance';
    static approveIchiAllowanceFNLabel = 'Approve ICHI';

    private constructor() {}

    public static getInstance(): IStake {
      if (!Stake.instance) {
        Stake.instance = new Stake();
      }
      return Stake.instance;
    }

    private getIchiContractContainer(args: StakeArgs) {
      const ichiContractContainer = TokenUtils.lookupIchiContracts(
        args.chainId
      )?.legacyIchiContainer;

      if (!ichiContractContainer) {
        const message = 'No ICHI contract container provided';
        const error = new Error(message);
        // TODO: consider removing or handle the error in the calling machine
        console.warn(error);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: 'stakeUnstakeApi',
          },
        });
        throw error;
      }

      return ichiContractContainer;
    }

    private getStakingContractContainer(args: StakeArgs) {
      const stakingContractContainer = TokenUtils.lookupIchiStakingContracts(
        args.chainId
      );
      if (!stakingContractContainer) {
        const message = 'No xICHI staking contract container provided';
        const error = new Error(message);
        // TODO: consider removing or handle the error in the calling machine
        console.warn(error);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: 'stakeUnstakeApi',
          },
        });
        throw error;
      }
      return stakingContractContainer;
    }

    async stake(args: StakeArgs): Promise<Nullable<StorageUtils.StoredTx>> {
      let storedTx: Nullable<StorageUtils.StoredTx> = null;

      const ichiContractContainer = this.getIchiContractContainer(args);
      const stakingContractContainer = this.getStakingContractContainer(args);

      const functionName = StakeApi.Stake.stakeFN;
      const prettyAmount = numbro(Number(args.amount)).format({
        thousandSeparated: true,
        mantissa: 2,
      });
      const labels: StorageUtils.LabelsArg = {
        labels: {
          executing: `Staking ${prettyAmount} ${ichiContractContainer.token.symbol}...`,
          success: `Successfully staked ${prettyAmount} ${ichiContractContainer.token.symbol}`,
          error: `Error staking ${prettyAmount} ${ichiContractContainer.token.symbol}`,
        },
      };

      if (
        isIchiStakingContract(stakingContractContainer.token, {
          ...args,
          container: stakingContractContainer,
        })
      ) {
        try {
          // Otherwise there is no tx in flight, execute contract call
          const stringBN = NumberUtils.stringToBN(args.amount, 9n);

          const tx = await writeContract({
            address: stakingContractContainer.token.address,
            abi: stakingContractContainer.abi,
            functionName: 'enter',
            args: [stringBN],
            gas: CryptoUtils.calculateGasMargin(
              BigInt(GAS_SETTINGS.stake),
              args.chainId
            ),
          });

          storedTx = await StorageUtils.storeTx({
            ...args,
            hash: tx.hash,
            functionName,
            labels: labels.labels,
            value: args.amount,
          });

          IchiInsights.trackEvent({
            event: 'STAKE' as any,
            properties: {
              ...args,
              amountBNString: stringBN,
              handledAt: `stakeUnstakeApi:${Stake.stakeFN}`,
              storedTx,
            },
          });
        } catch (error) {
          console.error(error);
          IchiInsights.trackException({
            error: error as Error,
            properties: {
              ...args,
              handledAt: `stakeUnstakeApi:${Stake.stakeFN}`,
              storedTx,
            },
          });
          throw error;
        }
      } else {
        const message = 'Could not stake, no contract specified';
        const error = new Error(message);
        console.error(error);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            storedTx,
            handledAt: `stakeUnstakeApi:${Stake.stakeFN}`,
          },
        });
        throw error;
      }

      return storedTx;
    }

    async unstake(args: UnstakeArgs): Promise<Nullable<StorageUtils.StoredTx>> {
      let storedTx: Nullable<StorageUtils.StoredTx> = null;

      const stakingContractContainer = this.getStakingContractContainer(args);

      const functionName = StakeApi.Stake.unstakeFN;

      const prettyAmount = numbro(Number(args.amount)).format({
        thousandSeparated: true,
        mantissa: 2,
      });
      const labels: StorageUtils.LabelsArg = {
        labels: {
          executing: `Unstaking ${prettyAmount} ${stakingContractContainer.token.symbol}...`,
          success: `Successfully unstaked ${prettyAmount} ${stakingContractContainer.token.symbol}`,
          error: `Error unstaking ${prettyAmount} ${stakingContractContainer.token.symbol}`,
        },
      };

      if (
        isIchiStakingContract(stakingContractContainer.token, {
          ...args,
          container: stakingContractContainer,
        })
      ) {
        try {
          // Otherwise there is no tx in flight, execute contract call
          const stringBN = NumberUtils.stringToBN(args.amount, 9n);

          const tx = await writeContract({
            address: stakingContractContainer.token.address,
            abi: stakingContractContainer.abi,
            functionName: 'leave',
            args: [stringBN],
            gas: CryptoUtils.calculateGasMargin(
              BigInt(GAS_SETTINGS.unstake),
              args.chainId
            ),
          });

          storedTx = await StorageUtils.storeTx({
            ...args,
            hash: tx.hash,
            functionName,
            labels: labels.labels,
            value: args.amount,
          });

          IchiInsights.trackEvent({
            event: StakeEvent.UNSTAKE,
            properties: {
              ...args,
              amount: args.amount,
              amountBNString: stringBN,
              handledAt: `stakeUnstakeApi:${Stake.unstakeFN}`,
              storedTx,
            },
          });
        } catch (error) {
          console.error(error);
          IchiInsights.trackException({
            error: error as Error,
            properties: {
              ...args,
              handledAt: `stakeUnstakeApi:${Stake.unstakeFN}`,
              storedTx,
            },
          });
          throw error;
        }
      } else {
        const message = 'Could not unstake, no contract specified';
        const error = new Error(message);
        console.error(error);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `stakeUnstakeApi:${Stake.unstakeFN}`,
            storedTx,
          },
        });
        throw error;
      }

      return storedTx;
    }

    async getStakeData(args: GetStakeDataArgs): Promise<GetStakeDataResponse> {
      const ichiContractContainer = this.getIchiContractContainer(args);
      const stakingContractContainer = this.getStakingContractContainer(args);

      if (!args.accountAddress) {
        const message = 'No wallet connected, could not get stake data';
        const error = new Error(message);
        // TODO: consider removing or handle the error in the calling machine
        console.warn(error);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: 'stakeUnstakeApi:getStakeData',
          },
        });
        throw error;
      }

      let ichiBalance = 0n;
      let ichiAllowance = 0n;
      let isIchiApproved = false;
      let isSufficientBalance = false;
      let ichiPriceUsd = 0;

      try {
        // TODO: As of 03/23 there is no staking with the new ICHI token on Ethereum and no staking on polygon
        // once we have those in place we'll need to update this to resolve the correct ICHI token
        const tokenAddress =
          ICHI_LEGACY_TOKENS[ChainId.MAINNET]?.address.toLowerCase();
        ichiPriceUsd = await TokenUtils.lookupTokenPrice(tokenAddress);
      } catch (e) {
        console.error('Could not fetch ichi price', e);
      }

      if (
        isIchiContract(ichiContractContainer.token, {
          container: ichiContractContainer,
          ...args,
        })
      ) {
        try {
          // First check the allowance

          ichiAllowance = await readContract({
            address: ichiContractContainer.token.address,
            abi: ichiContractContainer.abi,
            functionName: 'allowance',
            args: [args.accountAddress, stakingContractContainer.token.address],
            chainId: args.chainId,
          });

          ichiBalance = await readContract({
            address: ichiContractContainer.token.address,
            abi: ichiContractContainer.abi,
            functionName: 'balanceOf',
            args: [args.accountAddress],
            chainId: args.chainId,
          });

          const amountNumber = NumberUtils.stringToBN(args.amount, 9n);
          isSufficientBalance = ichiBalance >= amountNumber;

          // if it is 0, then flat out we need to show that it's not approved
          // Otherwise, ichiAllowance is greater than 0, and compare it to the amount given
          // TODO: Note that I've seen the amount come in as 0 even if it's technically not in the UI
          // This is likely a propagation of events issue
          isIchiApproved =
            ichiAllowance === 0n ? false : ichiAllowance >= amountNumber;
        } catch (error) {
          console.error(
            `Could not get stake data for: ${ichiContractContainer.token.symbol}`,
            error
          );
          IchiInsights.trackException({
            error: error as Error,
            properties: {
              ...args,
              handledAt: 'stakeUnstakeApi:getStakeData',
            },
          });
        }
      } else {
        console.warn(
          'Could not get stake data, given contract is not an ichi contract'
        );
      }

      const result: GetStakeDataResponse = {
        ichiBalance,
        ichiAllowance,
        isIchiApproved,
        isSufficientBalance,
        ichiPriceUsd,
      };
      // console.debug('stake data', result);
      return result;
    }

    async getUnstakeData(
      args: GetUnstakeDataArgs
    ): Promise<GetUnstakeDataResponse> {
      const stakingContractContainer = this.getStakingContractContainer(args);

      if (!args.accountAddress) {
        const message = 'No wallet connected, could not get stake data';
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: 'stakeUnstakeApi:getUnstakeData',
          },
        });
        throw error;
      }

      let xIchiBalance = 0n;
      let isSufficientBalance = false;
      let xIchiPriceUsd = 0;

      // TODO: this could be parallelized
      try {
        // TODO: As of 03/23 there is no staking with the new ICHI token on Ethereum and no staking on polygon
        // once we have those in place we'll need to update this to resolve the correct ICHI token
        const tokenAddress =
          ICHI_LEGACY_TOKENS[ChainId.MAINNET]?.address.toLowerCase();
        xIchiPriceUsd = await TokenUtils.lookupTokenPrice(tokenAddress);
      } catch (e) {
        console.error('Could not fetch xIchi price', e);
      }

      if (
        isIchiStakingContract(stakingContractContainer.token, {
          container: stakingContractContainer,
          ...args,
        })
      ) {
        try {
          const amountNumber = NumberUtils.stringToBN(args.amount, 9n);

          xIchiBalance = await readContract({
            address: stakingContractContainer.token.address,
            abi: stakingContractContainer.abi,
            functionName: 'balanceOf',
            args: [args.accountAddress],
            chainId: args.chainId,
          });

          isSufficientBalance = xIchiBalance >= amountNumber;
        } catch (error) {
          console.error(
            `Could not get unstake data for: ${stakingContractContainer.token.symbol}`,
            error
          );
          IchiInsights.trackException({
            error: error as Error,
            properties: {
              ...args,
              handledAt: 'stakeUnstakeApi:getUnstakeData',
            },
          });
        }
      }

      const result: GetUnstakeDataResponse = {
        xIchiBalance,
        isSufficientBalance,
        xIchiPriceUsd,
      };
      // console.debug('unstake data', result);
      return result;
    }
    // This function represents a blocking contract tx that supports local storage.  It will
    // block when called waiting for the tx to complete, however it still also supports local storage
    async resetIchiAllowance(args: GetStakeDataArgs): Promise<boolean> {
      const ichiContractContainer = this.getIchiContractContainer(args);
      const stakingContractContainer = this.getStakingContractContainer(args);

      const functionName = Stake.resetIchiAllowanceFN;

      const labels: StorageUtils.LabelsArg = {
        labels: {
          executing: `Resettting ${ichiContractContainer.token.symbol} Allowance...`,
          success: `Successfully reset ${ichiContractContainer.token.symbol} Allowance`,
          error: `Error Resetting ${stakingContractContainer.token.symbol} Allowance`,
        },
      };

      try {
        const storedTx = await StorageUtils.getTx({
          functionName,
          ...args,
        });

        // If there is a tx already in progress, wait for it here
        if (storedTx) {
          const didSucceed = await ContractUtils.waitForTransactionBoolean({
            functionName,
            hash: storedTx.hash,
            ...(args as any),
          });
          return didSucceed;
        }

        // If no prior tx, execute contract function
        const allowance = await readContract({
          address: ichiContractContainer.token.address,
          abi: ichiContractContainer.abi,
          functionName: 'allowance',
          args: [args.accountAddress, stakingContractContainer.token.address],
          chainId: args.chainId,
        });

        const tx = await writeContract({
          address: ichiContractContainer.token.address,
          abi: ichiContractContainer.abi,
          functionName: 'decreaseAllowance',
          args: [stakingContractContainer.token.address, allowance || 0n],
        });

        // Storage the tx hash in local storage
        if (tx) {
          await StorageUtils.storeTx({
            hash: tx.hash,
            functionName,
            labels: labels.labels,
            value: args.amount,
            ...args,
          });

          // Wait for the transaction to complete
          const didSucceed = await ContractUtils.waitForTransactionBoolean({
            functionName,
            hash: tx.hash,
            ...(args as any),
          });
          return didSucceed;
        }

        throw new Error(
          `Unable to reset ${stakingContractContainer.token.address} on ${ichiContractContainer.token.address} allowance due to infuraProvider being null`
        );
      } catch (err) {
        console.error(err);
        if (isUserRejectedError(err as BaseError)) {
          throw new Error((err as UserRejectedRequestError).shortMessage);
        }
        const error = isViemBaseError(err as BaseError)
          ? new Error((err as BaseError).shortMessage)
          : err;

        IchiInsights.trackException({
          error: error as Error,
          properties: {
            ...args,
            handledAt: `stakeUnstakeApi:${Stake.resetIchiAllowanceFN}`,
          },
        });
        throw error;
      }
    }

    async approveIchiAllowance(
      args: GetStakeDataArgs
    ): Promise<Nullable<StorageUtils.StoredTx>> {
      let storedTx: Nullable<StorageUtils.StoredTx> = null;

      const ichiContractContainer = this.getIchiContractContainer(args);
      const stakingContractContainer = this.getStakingContractContainer(args);

      const functionName = Stake.approveIchiAllowanceFN;

      const labels: StorageUtils.LabelsArg = {
        labels: {
          executing: `Approving ${ichiContractContainer.token.symbol}...`,
          success: `Successfully approved ${ichiContractContainer.token.symbol}`,
          error: `Error Approving ${stakingContractContainer.token.symbol}`,
        },
      };

      try {
        const tx = await writeContract({
          address: ichiContractContainer.token.address,
          abi: ichiContractContainer.abi,
          functionName: 'approve',
          args: [
            stakingContractContainer.token.address,
            BigInt(Stake.maxApproval),
          ],
          gas: CryptoUtils.calculateGasMargin(
            BigInt(GAS_SETTINGS.approve),
            args.chainId
          ),
        });

        if (tx) {
          storedTx = await StorageUtils.storeTx({
            ...args,
            hash: tx.hash,
            functionName,
            labels: labels.labels,
            value: args.amount,
          });

          IchiInsights.trackEvent({
            event: StakeEvent.APPROVE_ICHI_ALLOWANCE,
            properties: {
              ...args,
              amount: Stake.maxApproval.toString(),
              handledAt: `stakeUnstakeApi:${Stake.approveIchiAllowanceFN}`,
              storedTx,
            },
          });
        }
      } catch (error) {
        console.error(error);
        IchiInsights.trackException({
          error: error as Error,
          properties: {
            ...args,
            handledAt: `stakeUnstakeApi:${Stake.approveIchiAllowanceFN}`,
          },
        });
        throw error;
      }
      return storedTx;
    }
  }
}
